import React, { useState } from "react";
import SVGraphics from "../../../assets/SVGraphics";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import RadiologistApi from "../../../services/ApiServices/RadiologistApi";
import { useViewer } from "../DicomViewerNew/context/ViewerContext";

dayjs.extend(advancedFormat);

const AnnotationTile = ({
  annotation,
  type,
  currentUser,
  currentImageIndex,
  currentSlice,
  onDeleted,
  goToSlice,
}) => {
  const { selectedAnnotationId, setSelectedAnnotationId, zoomToAnnotation } =
    useViewer();

  const [isEditing, setIsEditing] = useState(false);
  const [draftNote, setDraftNote] = useState(annotation.note);
  const isSelected = selectedAnnotationId === annotation.id;

  // Compute midpoint or fallback to x/y for findings
  let midpoint = null;

  if (annotation.start && annotation.end) {
    const sx =
      annotation.start.x ??
      (Array.isArray(annotation.start) ? annotation.start[0] : null);
    const sy =
      annotation.start.y ??
      (Array.isArray(annotation.start) ? annotation.start[1] : null);
    const ex =
      annotation.end.x ??
      (Array.isArray(annotation.end) ? annotation.end[0] : null);
    const ey =
      annotation.end.y ??
      (Array.isArray(annotation.end) ? annotation.end[1] : null);

    if (sx != null && sy != null && ex != null && ey != null) {
      midpoint = { x: (sx + ex) / 2, y: (sy + ey) / 2 };
    }
  }

  if (
    !midpoint &&
    typeof annotation.x === "number" &&
    typeof annotation.y === "number"
  ) {
    midpoint = { x: annotation.x, y: annotation.y };
  }

  const getTitle = () => {
    if (type === "finding") return "Genesis Finding";
    if (type === "personal") return "Personal Note";
    return "Measuring Note";
  };

  const getIcon = () => {
    if (type === "finding") {
      return isSelected
        ? "GenesisAnnotationIconActive"
        : "GenesisAnnotationIcon";
    }
    if (type === "personal") {
      return isSelected ? "personalNoteSelected" : "personalNote";
    }
    if (type === "measurement") {
      return isSelected
        ? "MeasureAnnotationIconActive"
        : "MeasureAnnotationIcon";
    }
    return "";
  };

  const handleSave = async () => {
    try {
      await RadiologistApi.updateAnnotation({
        id: annotation.id,
        note: draftNote,
        creator: `${currentUser.profile.First_name} ${currentUser.profile.Last_name}`,
      });
      annotation.note = draftNote;
      annotation.updatedAt = new Date().toISOString();
      setIsEditing(false);
    } catch (err) {
      console.error("Error updating annotation:", err);
    }
  };

  const handleDelete = async () => {
    try {
      await RadiologistApi.deleteAnnotation({ id: annotation.id });
      onDeleted(annotation.id);
      setIsEditing(false);
    } catch (err) {
      console.error("Error deleting annotation:", err);
    }
  };

  const handleSelect = () => {
    if (isEditing || draftNote === "Deleted note.") return;

    if (currentImageIndex !== annotation.slice) {
      goToSlice(annotation.slice);
    }
    if (!isSelected) {
      setSelectedAnnotationId(annotation.id);
      zoomToAnnotation(annotation); // zoom and center
    } else {
      setSelectedAnnotationId(null);
      zoomToAnnotation(null); // reset zoom
    }
  };

  return (
    <div
      key={annotation.id}
      className={`annotation-tile max-w-[15rem] cursor-pointer flex flex-col py-[15px] pl-[8px] pr-[9px] text-white ${
        isSelected
          ? "bg-[#5A3EFF] rounded-[10px]"
          : "bg-transparent border-b border-[#2B2B2B]"
      }`}
      onClick={handleSelect}>
      {/* Header */}
      <div className="flex items-center justify-between py-2">
        <div className="flex items-center gap-2">
          <SVGraphics svgname={getIcon()} width="20" height="20" />
          <span className="font-semibold text-base">{getTitle()}</span>
        </div>

        {!isEditing && getTitle() !== "Genesis Finding" && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsEditing(true);
            }}
            className="text-blue-400 text-sm font-medium">
            Edit
          </button>
        )}
        {isEditing && draftNote !== "Deleted note." && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setDraftNote("Deleted note.");
            }}
            className="text-red-400 text-sm font-medium">
            Delete
          </button>
        )}
      </div>

      {/* Body */}
      {isEditing ? (
        draftNote === "Deleted note." ? (
          <div className="space-y-2">
            <div className="text-gray-400 text-xs">
              Are you sure you want to delete this note?
            </div>
            <div className="flex gap-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setDraftNote(annotation.note);
                  setIsEditing(false);
                }}
                className="text-[12px] text-white border rounded-[8px] w-[81px] h-[32px]">
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="text-[12px] bg-[#5A3EFF] rounded-[8px] w-[81px] h-[32px]">
                Yes, delete
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="flex justify-between text-gray-400 text-xs">
              <span>{annotation.creator} |</span>
              <span>
                {dayjs(annotation.updatedAt).format("DD.MM.YYYY - HH:mm")}
              </span>
            </div>
            <div className="text-gray-400 text-xs"># Slice {currentSlice}</div>
            <textarea
              className="bg-black w-full text-[12px] h-[100px] p-[9px] rounded-[10px]"
              value={draftNote}
              onChange={(e) => setDraftNote(e.target.value)}
            />
            <div className="flex flex-row-reverse gap-2 mt-2">
              <button
                onClick={handleSave}
                className="text-[12px] bg-[#5A3EFF] rounded-[8px] w-[81px] h-[32px]">
                Save
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setDraftNote(annotation.note);
                  setIsEditing(false);
                }}
                className="text-[12px] text-white border rounded-[8px] w-[81px] h-[32px]">
                Cancel
              </button>
            </div>
          </>
        )
      ) : (
        // In the non-editing view section:
        <div className="py-2 space-y-1">
          <div className="flex text-gray-400 text-xs">
            <span className="whitespace-nowrap">{annotation.creator}</span>
            <span className="mx-2">|</span>
            <span className="whitespace-nowrap">
              {dayjs(annotation.updatedAt).format("DD.MM.YYYY - HH:mm")}
            </span>
          </div>
          <div className="text-gray-400 text-xs"># Slice {currentSlice}</div>

          {/* Add length display for measurements - same styling as slice */}
          {type === "measurement" && annotation.length && (
            <div className="text-gray-400 text-xs">
              Length: {annotation.length.toFixed(1)} mm
            </div>
          )}

          <div className="text-sm whitespace-pre-wrap break-words">
            {annotation.note}
          </div>
        </div>
      )}
    </div>
  );
};

export default AnnotationTile;
